import { forwardRef } from 'react';
import {
  type DimensionValue,
  Pressable,
  type PressableProps,
  StyleSheet,
  View,
  type ViewProps,
} from 'react-native';
import { type SpacingScale, spacing } from '../../theme/spacing';
import { type RadiusKey, type ShadowKey, tokens } from '../../theme/tokens';

interface BoxProps extends ViewProps {
  padding?: SpacingScale;
  paddingX?: SpacingScale;
  paddingY?: SpacingScale;
  paddingTop?: SpacingScale;
  paddingRight?: SpacingScale;
  paddingBottom?: SpacingScale;
  paddingLeft?: SpacingScale;

  margin?: SpacingScale;
  marginX?: SpacingScale;
  marginY?: SpacingScale;
  marginTop?: SpacingScale;
  marginRight?: SpacingScale;
  marginBottom?: SpacingScale;
  marginLeft?: SpacingScale;

  flex?: number;
  flexDirection?: 'row' | 'column' | 'row-reverse' | 'column-reverse';
  flexWrap?: 'wrap' | 'nowrap' | 'wrap-reverse';
  alignItems?: 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  alignSelf?: 'auto' | 'flex-start' | 'flex-end' | 'center' | 'stretch' | 'baseline';
  justifyContent?:
    | 'flex-start'
    | 'flex-end'
    | 'center'
    | 'space-between'
    | 'space-around'
    | 'space-evenly';

  gap?: SpacingScale;
  rowGap?: SpacingScale;
  columnGap?: SpacingScale;

  width?: DimensionValue;
  height?: DimensionValue;
  minWidth?: DimensionValue;
  minHeight?: DimensionValue;
  maxWidth?: DimensionValue;
  maxHeight?: DimensionValue;

  backgroundColor?: string;
  borderRadius?: RadiusKey | number;
  borderWidth?: number;
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  borderColor?: string;
  borderTopColor?: string;
  borderRightColor?: string;
  borderBottomColor?: string;
  borderLeftColor?: string;

  shadow?: ShadowKey;
  opacity?: number;
  overflow?: 'visible' | 'hidden' | 'scroll';

  position?: 'absolute' | 'relative';
  top?: DimensionValue;
  right?: DimensionValue;
  bottom?: DimensionValue;
  left?: DimensionValue;
  zIndex?: number;

  pressable?: boolean;
  onPress?: PressableProps['onPress'];
  onPressIn?: PressableProps['onPressIn'];
  onPressOut?: PressableProps['onPressOut'];
  onLongPress?: PressableProps['onLongPress'];
  hitSlop?: PressableProps['hitSlop'];
  disabled?: boolean;
}

function resolveSpacing(scale: SpacingScale | undefined) {
  return scale === undefined ? undefined : spacing[scale];
}

// `gap` is supported natively on iOS, web, and Android API 21+ from RN 0.71+.
// Expo 54 ships RN 0.81, so it's universally available. The prior guard read
// `typeof Platform.Version === 'number'` which is always falsy on iOS
// (Platform.Version is a string there: '17.0' etc.), silently dropping the
// gap style on every HStack/VStack/Box on iOS — visibly cramped milestone
// rows on the Dashboard onboarding card and elsewhere.
const supportsGap = true;

function buildBoxStyle(props: BoxProps) {
  return {
    padding: resolveSpacing(props.padding),
    paddingHorizontal: resolveSpacing(props.paddingX),
    paddingVertical: resolveSpacing(props.paddingY),
    paddingTop: resolveSpacing(props.paddingTop),
    paddingRight: resolveSpacing(props.paddingRight),
    paddingBottom: resolveSpacing(props.paddingBottom),
    paddingLeft: resolveSpacing(props.paddingLeft),

    margin: resolveSpacing(props.margin),
    marginHorizontal: resolveSpacing(props.marginX),
    marginVertical: resolveSpacing(props.marginY),
    marginTop: resolveSpacing(props.marginTop),
    marginRight: resolveSpacing(props.marginRight),
    marginBottom: resolveSpacing(props.marginBottom),
    marginLeft: resolveSpacing(props.marginLeft),

    flex: props.flex,
    flexDirection: props.flexDirection,
    flexWrap: props.flexWrap,
    alignItems: props.alignItems,
    alignSelf: props.alignSelf,
    justifyContent: props.justifyContent,

    ...(supportsGap
      ? {
          gap: resolveSpacing(props.gap),
          rowGap: resolveSpacing(props.rowGap),
          columnGap: resolveSpacing(props.columnGap),
        }
      : {}),

    width: props.width,
    height: props.height,
    minWidth: props.minWidth,
    minHeight: props.minHeight,
    maxWidth: props.maxWidth,
    maxHeight: props.maxHeight,

    backgroundColor: props.backgroundColor,
    borderRadius:
      typeof props.borderRadius === 'string'
        ? tokens.radius[props.borderRadius]
        : props.borderRadius,
    borderWidth: props.borderWidth,
    borderTopWidth: props.borderTopWidth,
    borderRightWidth: props.borderRightWidth,
    borderBottomWidth: props.borderBottomWidth,
    borderLeftWidth: props.borderLeftWidth,
    borderColor: props.borderColor,
    borderTopColor: props.borderTopColor,
    borderRightColor: props.borderRightColor,
    borderBottomColor: props.borderBottomColor,
    borderLeftColor: props.borderLeftColor,
    opacity: props.opacity,
    overflow: props.overflow,

    position: props.position,
    top: props.top,
    right: props.right,
    bottom: props.bottom,
    left: props.left,
    zIndex: props.zIndex,

    ...(props.shadow ? tokens.shadow?.[props.shadow] : {}),
  };
}

export const Box = forwardRef<View, BoxProps>(
  (
    {
      pressable,
      onPress,
      onPressIn,
      onPressOut,
      onLongPress,
      hitSlop,
      disabled,
      style,
      children,
      ...rest
    },
    ref,
  ) => {
    const computedStyle = StyleSheet.flatten([buildBoxStyle(rest as BoxProps), style]);

    if (pressable) {
      return (
        <Pressable
          ref={ref as any}
          style={computedStyle}
          onPress={onPress}
          onPressIn={onPressIn}
          onPressOut={onPressOut}
          onLongPress={onLongPress}
          hitSlop={hitSlop}
          disabled={disabled}
          {...rest}
        >
          {children}
        </Pressable>
      );
    }

    return (
      <View ref={ref} style={computedStyle} {...rest}>
        {children}
      </View>
    );
  },
);

Box.displayName = 'Box';
